import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useOnboardingProgress } from './useOnboardingProgress';
import {
  GUIDE_STEPS,
  ALL_SECTIONS,
  filterStepsForUser,
  getAvailableSections,
  mapTemplateToUseCase,
} from './guideSteps';
import type {
  GuideStep,
  OnboardingContextValue,
  PageKey,
  SectionKey,
  UserPreferences,
} from './types';

// eslint-disable-next-line react-refresh/only-export-components
export const OnboardingContext = createContext<OnboardingContextValue | null>(null);

const SESSION_ACTIVE_KEY = 'fw-onboarding-session-active';

interface Props {
  children: ReactNode;
  /** Plan tier of current user (for gating advanced sections). Defaults to 'free'. */
  planTier?: string;
}

export function OnboardingProvider({ children, planTier = 'free' }: Props) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { language } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { progress, hydrated, update, updatePreferences, reset } = useOnboardingProgress(user?.id ?? null);

  const [isActive, setIsActive] = useState(false);
  const [introDialogOpen, setIntroDialogOpen] = useState(false);
  const [activePage, setActivePage] = useState<PageKey | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [openModalCount, setOpenModalCount] = useState(0);
  const pushOpenModal = useCallback(() => setOpenModalCount(c => c + 1), []);
  const popOpenModal = useCallback(() => setOpenModalCount(c => Math.max(0, c - 1)), []);
  const [pending, setPending] = useState<{ section: SectionKey; resumeAt: number } | null>(null);
  const prevRouteRef = useRef(location.pathname);

  const filterCtx = useMemo(() => ({
    preferences: progress?.preferences ?? null,
    planTier,
    isMobile,
    isAuthenticated,
  }), [progress?.preferences, planTier, isMobile, isAuthenticated]);

  const steps = useMemo<GuideStep[]>(
    () => (activeSection ? filterStepsForUser(GUIDE_STEPS, activeSection, filterCtx) : []),
    [activeSection, filterCtx],
  );

  const availableSections = useMemo(() => getAvailableSections(filterCtx), [filterCtx]);

  // With WorkflowWizardDialog handling the intro, we no longer auto-show our own intro dialog.
  const shouldShowIntro = false;
  const shouldShowGenericTour =
    hydrated && !authLoading && !isAuthenticated && !!progress && !progress.dismissed_welcome && !progress.skipped && !wizardOpen && openModalCount === 0;

  const armTour = useCallback((section: SectionKey, resumeAt: number) => {
    const sectionDef = ALL_SECTIONS.find(s => s.id === section);
    // Clamp resumeAt so a half-completed tour whose step count changed
    // (filter rules updated, GUIDE_STEPS edited) still has a valid index.
    // `0` is always safe because every section has at least a first step.
    const availableSteps = filterStepsForUser(GUIDE_STEPS, section, filterCtx);
    const safeResumeAt = Number.isFinite(resumeAt) && resumeAt >= 0 && resumeAt < availableSteps.length
      ? resumeAt
      : 0;
    setActivePage(sectionDef?.page ?? 'index');
    setActiveSection(section);
    setCurrentStepIndex(safeResumeAt);
    setIsActive(true);
    try { sessionStorage.setItem(SESSION_ACTIVE_KEY, '1'); } catch { /* */ }
    update({ current_section: section, current_step_index: safeResumeAt });
    // Tours that want concrete demo blocks ask the page to seed them. The
    // page ignores this if the canvas already has content.
    const DEMO_SECTIONS: SectionKey[] = ['welcome', 'blocks', 'connectors', 'groups-layers', 'layer-nav'];
    if (DEMO_SECTIONS.includes(section)) {
      // Deferred so the page mounts before the seed arrives.
      setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent('fw-onboarding-event', {
            detail: { name: 'seed:request', data: { section } },
          }));
        } catch { /* no-op */ }
      }, 200);
    }
  }, [update, filterCtx]);

  const startTour = useCallback<OnboardingContextValue['startTour']>((section, opts) => {
    const sectionDef = ALL_SECTIONS.find(s => s.id === section);
    const page = sectionDef?.page ?? 'index';
    const targetPath = page === 'admin' ? '/admin' : page === 'deploy' ? '/deploy' : '/';
    const resumeAt = opts?.fromStep ?? (progress?.current_section === section ? progress.current_step_index : 0);
    if (location.pathname !== targetPath) {
      setPending({ section, resumeAt });
      setIsActive(false);
      navigate(targetPath);
      return;
    }
    armTour(section, resumeAt);
  }, [progress, location.pathname, navigate, armTour]);

  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (!cancelled) {
        armTour(pending.section, pending.resumeAt);
        setPending(null);
      }
    }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pending, location.pathname, armTour]);

  useEffect(() => {
    if (isActive && location.pathname !== prevRouteRef.current) {
      setIsActive(false);
      setPending(null);
      try { sessionStorage.removeItem(SESSION_ACTIVE_KEY); } catch { /* */ }
    }
    prevRouteRef.current = location.pathname;
  }, [location.pathname, isActive]);

  const markSectionCompleted = useCallback((s: SectionKey) => {
    if (!progress) return;
    const alreadyIn = progress.completed_sections.includes(s);
    update({
      completed_sections: alreadyIn ? progress.completed_sections : [...progress.completed_sections, s],
    });
  }, [progress, update]);

  const endTour = useCallback(() => {
    setIsActive(false);
    setPending(null);
    try { sessionStorage.removeItem(SESSION_ACTIVE_KEY); } catch { /* */ }
    // Tell the page to tear down the tour-sandbox layer (isTourSandbox=true).
    try {
      window.dispatchEvent(new CustomEvent('fw-onboarding-event', {
        detail: { name: 'tour:end' },
      }));
    } catch { /* no-op */ }
  }, []);

  const next = useCallback(() => {
    setCurrentStepIndex(i => {
      const n = i + 1;
      if (n >= steps.length) {
        setIsActive(false);
        if (activeSection) markSectionCompleted(activeSection);
        try {
          window.dispatchEvent(new CustomEvent('fw-onboarding-event', {
            detail: { name: 'tour:end' },
          }));
        } catch { /* no-op */ }
        return i;
      }
      update({ current_step_index: n });
      return n;
    });
  }, [steps.length, activeSection, markSectionCompleted, update]);

  const skipStep = useCallback(() => {
    setCurrentStepIndex(i => {
      const n = i + 1;
      if (n >= steps.length) { setIsActive(false); return i; }
      return n;
    });
  }, [steps.length]);

  const prev = useCallback(() => setCurrentStepIndex(i => Math.max(0, i - 1)), []);
  const goTo = useCallback((i: number) => setCurrentStepIndex(Math.max(0, Math.min(steps.length - 1, i))), [steps.length]);

  const openIntroDialog = useCallback(() => setIntroDialogOpen(true), []);
  const closeIntroDialog = useCallback(() => setIntroDialogOpen(false), []);

  const completeIntro = useCallback((preferences: UserPreferences) => {
    update({
      preferences,
      dismissed_welcome: true,
      started_at: progress?.started_at || new Date().toISOString(),
    });
    setIntroDialogOpen(false);
    setTimeout(() => armTour('welcome', 0), 50);
  }, [update, progress?.started_at, armTour]);

  const skipOnboarding = useCallback(() => {
    update({
      skipped: true,
      dismissed_welcome: true,
      completed_at: new Date().toISOString(),
    });
    setIntroDialogOpen(false);
    setIsActive(false);
    try {
      window.dispatchEvent(new CustomEvent('fw-onboarding-event', {
        detail: { name: 'tour:end' },
      }));
    } catch { /* no-op */ }
  }, [update]);

  /**
   * Triggered by WorkflowWizardDialog.onComplete. Maps templateId → UseCase, persists
   * preferences with safe defaults, and opens TourPicker (except for 'advanced' which
   * skips onboarding entirely while keeping the floating HelpButton accessible).
   */
  const completeIntroFromTemplate = useCallback((templateId: string) => {
    const useCase = mapTemplateToUseCase(templateId);
    const now = new Date().toISOString();
    const prefs: UserPreferences = {
      experience: progress?.preferences?.experience ?? 'beginner',
      role: progress?.preferences?.role ?? 'other',
      useCase,
      interests: progress?.preferences?.interests ?? [],
    };
    if (useCase === 'sandbox') {
      // Advanced/Blank Canvas: skip onboarding. HelpButton FAB remains so user can still open TourPicker.
      update({
        preferences: prefs,
        skipped: true,
        dismissed_welcome: true,
        started_at: progress?.started_at || now,
        completed_at: now,
      });
      setPickerVisible(false);
      return;
    }
    // Wizard completion no longer auto-opens the TourPicker. Users open it
    // explicitly via the floating "?" HelpButton in the corner.
    update({
      preferences: prefs,
      dismissed_welcome: true,
      skipped: true,
      started_at: progress?.started_at || now,
      completed_at: now,
    });
    setPickerVisible(false);
  }, [progress, update]);

  const resetAll = useCallback(() => {
    reset();
    setIsActive(false);
    setIntroDialogOpen(false);
    setActiveSection(null);
    setCurrentStepIndex(0);
  }, [reset]);

  const value = useMemo<OnboardingContextValue>(() => ({
    lang: language,
    isActive,
    introDialogOpen,
    activePage,
    activeSection,
    currentStepIndex,
    steps,
    progress,
    shouldShowIntro,
    shouldShowGenericTour,
    pickerVisible,
    setPickerVisible,
    wizardOpen,
    setWizardOpen,
    openModalCount,
    pushOpenModal,
    popOpenModal,
    openIntroDialog,
    closeIntroDialog,
    completeIntro,
    completeIntroFromTemplate,
    skipOnboarding,
    startTour,
    endTour,
    next,
    prev,
    skipStep,
    goTo,
    markSectionCompleted,
    updatePreferences,
    resetAll,
    allSections: availableSections,
  }), [
    language, isActive, introDialogOpen, activePage, activeSection, currentStepIndex, steps,
    progress, shouldShowIntro, shouldShowGenericTour, pickerVisible, wizardOpen, openModalCount, pushOpenModal, popOpenModal,
    openIntroDialog, closeIntroDialog, completeIntro, completeIntroFromTemplate, skipOnboarding,
    startTour, endTour, next, prev, skipStep, goTo, markSectionCompleted, updatePreferences, resetAll,
    availableSections,
  ]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}
